import { useAppDispatch } from '../../../../../redux/hooks';
import { playGame } from '../../../../../redux/slices/gameParrotSlice';
import { getOccButtons, numToCoords } from '../../gameUtils';
import useTicTacToeModalStyles from '../useTicTacToeModalStyles';

/**
 * Interface representing the props for the Tic Tac Toe Button component.
 *
 * - idx The number of the button to help determine its coordinates on the board.
 * - selfId The user ID of the user whose board this button is a part of.
 * - oppId The user ID of the user whose board this button is not a part of.
 * - gameStatus Array containing information for every turn taken in the game.
 * - challenger Boolean representing whether or not the user viewing the board
 *   is the challenger.
 * - canPlay Boolean representing whether or not the user can take their turn.
 */
interface TicTacToeButtonProps {
  idx: number;
  selfId: string;
  oppId: string;
  gameStatus: (string | number | number[][])[][];
  challenger: boolean | undefined;
  canPlay: boolean;
}

/**
 * Tic Tac Toe Button component renders a button, many of which make up a tic tac toe game board.
 *
 * - @param idx The number of the button to help determine its coordinates on the board.
 * - @param selfId The user ID of the user whose board this button is a part of.
 * - @param oppId The user ID of the user whose board this button is not a part of.
 * - @param gameStatus Array containing information for every turn taken in the game.
 * - @param challenger Boolean representing whether or not the user viewing the board
 *   @param is the challenger.
 * - @param canPlay Boolean representing whether or not the user can take their turn.
 */
const TicTacToeButton = ({
  idx,
  selfId,
  oppId,
  gameStatus,
  challenger,
  canPlay,
}: TicTacToeButtonProps) => {
  const { classes } = useTicTacToeModalStyles();
  const dispatch = useAppDispatch();

  /**
   * Function to handle the clicking of a tic tac toe button which notifies the opponent
   * of the move that the user has made.
   */
  const handleClick = () => {
    if (
      canPlay &&
      !getOccButtons(selfId, gameStatus, 3).includes(idx) &&
      !getOccButtons(oppId, gameStatus, 3).includes(idx)
    )
      dispatch(
        playGame({
          going: selfId,
          waiting: oppId,
          data: numToCoords(idx, 3),
          type: 'turn',
        }),
      );
  };

  return (
    <button
      className={`${classes.button}
        ${getOccButtons(selfId, gameStatus, 3).includes(idx) && challenger && classes.occByChallenger}
        ${getOccButtons(selfId, gameStatus, 3).includes(idx) && !challenger && classes.occByChallenged}
        ${getOccButtons(oppId, gameStatus, 3).includes(idx) && challenger && classes.occByChallenged}
        ${getOccButtons(oppId, gameStatus, 3).includes(idx) && !challenger && classes.occByChallenger}
        ${
          !getOccButtons(selfId, gameStatus, 3).includes(idx) &&
          !getOccButtons(oppId, gameStatus, 3).includes(idx) &&
          classes.unOcc
        }`}
      onClick={handleClick}
    />
  );
};

export default TicTacToeButton;
